import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { ServiceCategoryResponse } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

interface FilterValues {
  categoryId: string;
  status: string;
}

interface ServiceFiltersProps {
  onApply: (filters: FilterValues) => void;
  onClose: () => void;
  initialFilters: FilterValues;
}

function ServiceFilters({ onApply, onClose, initialFilters }: ServiceFiltersProps) {
  const { t } = useLanguage();
  const [categories, setCategories] = useState<ServiceCategoryResponse[]>([]);
  const [categoryId, setCategoryId] = useState(initialFilters.categoryId);
  const [status, setStatus] = useState(initialFilters.status);

  useEffect(() => {
    api.getCategories().then(setCategories).catch(() => {});
  }, []);

  const parentCategories = categories.filter((c) => !c.parentId);

  const handleReset = () => {
    setCategoryId('');
    setStatus('');
  };

  const handleApply = () => {
    onApply({ categoryId, status });
    onClose();
  };

  return (
    <div className="partner-modal-overlay">
      <div className="partner-modal" style={{ maxWidth: '400px' }}>
        <div className="partner-modal-header">
          <h2>{t('services.filters.title')}</h2>
          <button className="partner-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="partner-modal-body">
          <div className="partner-modal-form-group">
            <label>{t('services.modal.category')}</label>
            <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">{t('services.filters.allCategories')}</option>
              {parentCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="partner-modal-form-group">
            <label>{t('services.modal.status')}</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('services.filters.allStatuses')}</option>
              <option value="ACTIVE">{t('services.status.active')}</option>
              <option value="INACTIVE">{t('services.status.inactive')}</option>
              <option value="PENDING_APPROVAL">{t('services.status.pending')}</option>
            </select>
          </div>
        </div>

        <div className="partner-modal-footer" style={{ justifyContent: 'space-between' }}>
          <button className="partner-filter-reset" onClick={handleReset}>
            {t('services.filters.reset')}
          </button>
          <button className="partner-modal-btn-primary" onClick={handleApply}>
            {t('services.filters.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface SortOption {
  value: string;
  label: string;
}

interface SortDropdownProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}

export function SortDropdown({ value, onChange, onClose }: SortDropdownProps) {
  const { t } = useLanguage();

  const options: SortOption[] = [
    { value: '', label: t('services.sort.default') },
    { value: 'price_asc', label: t('services.sort.priceAsc') },
    { value: 'price_desc', label: t('services.sort.priceDesc') },
  ];

  return (
    <div className="partner-modal-overlay">
      <div className="partner-modal" style={{ maxWidth: '300px' }}>
        <div className="partner-modal-header">
          <h2>{t('services.sort.title')}</h2>
          <button className="partner-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="partner-modal-body">
          {options.map((opt) => (
            <label key={opt.value} className="partner-modal-radio-label" style={{ display: 'flex', padding: '0.5rem 0' }}>
              <input
                type="radio"
                name="sort"
                checked={value === opt.value}
                onChange={() => { onChange(opt.value); onClose(); }}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ServiceFilters;
